import { displayDateUS } from "../../utils/dateUtils";
import { competencyForPositionLabel } from "../../pages/Application/competencyPositionTitle";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function ynDisplay(v) {
  if (v === true || v === "true") return "Y";
  if (v === false || v === "false") return "N";
  return "—";
}

function hhaRatingDisplay(v) {
  if (v === "1" || v === 1) return "1 — None";
  if (v === "2" || v === 2) return "2 — Review";
  if (v === "3" || v === 3) return "3 — Indep.";
  if (v != null && v !== "") return escapeHtml(String(v));
  return "—";
}

/**
 * Matches BaseCompetency.jsx fetch (`result.competency`, `result.data`, or `result`).
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseCompetencyFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.competency) {
    return result.competency;
  }
  if (result?.data) {
    return result.data;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.competencyData != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/** @param {Record<string, unknown>} record */
function getSections(record) {
  const cd = record.competencyData;
  const nested =
    cd && typeof cd === "object" && !Array.isArray(cd)
      ? /** @type {{ sections?: unknown }} */ (cd).sections
      : null;
  if (Array.isArray(nested)) return nested;
  if (Array.isArray(record.sections)) return record.sections;
  return [];
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * @param {Record<string, unknown>} item
 * @param {boolean} isHha
 */
function buildCompetencyRowCells(item, isHha) {
  const skill = escapeHtml(
    String(item.skill || item.competency || "").trim(),
  );
  const method = escapeHtml(String(item.method || item.evaluationMethod || ""));
  const initials = escapeHtml(
    String(item.preceptorInitials || item.evaluatorInitials || ""),
  );
  const rowDate = escapeHtml(
    formatDate(item.preceptorDate || item.evaluatorDate),
  );

  if (isHha) {
    const rating = hhaRatingDisplay(item.hhaSelfRating);
    const met = ynDisplay(item.supervisorCompetencyMet);
    return `<tr>
      <td class="c-td c-skill">${skill || "—"}</td>
      <td class="c-td c-c">${rating}</td>
      <td class="c-td c-c">${method || "—"}</td>
      <td class="c-td c-c">${met}</td>
      <td class="c-td c-c">${initials || "—"}</td>
      <td class="c-td c-c">${rowDate || "—"}</td>
    </tr>`;
  }

  const prof =
    item.proficiencyRequired === true || item.proficiencyRequired === "true";
  const compText = `${skill}${prof ? ' <span class="req">*</span>' : ""}`;
  const exp = ynDisplay(item.hasExperience);
  const comp = ynDisplay(item.isCompetent);
  const evalM = escapeHtml(String(item.evaluationMethod || ""));

  return `<tr>
    <td class="c-td c-c">${exp}</td>
    <td class="c-td c-c">${comp}</td>
    <td class="c-td c-skill">${compText || "—"}</td>
    <td class="c-td">${evalM || "—"}</td>
    <td class="c-td c-c">${initials || "—"}</td>
    <td class="c-td c-c">${rowDate || "—"}</td>
  </tr>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildCompetencyPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const roleRaw = String(record.role || "").trim();
  const roleUpper = roleRaw.toUpperCase();
  const isHha = roleUpper === "HHA";

  const employeeName = escapeHtml(
    String(record.name || record.applicantName || "").trim(),
  );
  const roleDisp = escapeHtml(roleRaw || "—");
  const dateEmp = escapeHtml(formatDate(record.dateOfEmployment));
  const dateDone = escapeHtml(formatDate(record.dateCompleted));

  const sections = getSections(record);

  const competencyCol = escapeHtml(competencyForPositionLabel(roleRaw));
  const theadHha = `<thead><tr>
    <th class="c-th">Skill</th>
    <th class="c-th c-c">Self rating</th>
    <th class="c-th c-c">Method</th>
    <th class="c-th c-c">Met (Y/N)</th>
    <th class="c-th c-c">Initials</th>
    <th class="c-th c-c">Date</th>
  </tr></thead>`;

  const theadRn = `<thead><tr>
    <th class="c-th c-c">Exp (Y/N)</th>
    <th class="c-th c-c">Comp (Y/N)</th>
    <th class="c-th">${competencyCol}</th>
    <th class="c-th">Eval method</th>
    <th class="c-th c-c">Initials</th>
    <th class="c-th c-c">Date</th>
  </tr></thead>`;

  const blocksHtml = sections
    .map((sec) => {
      if (!sec || typeof sec !== "object") return "";
      const title = escapeHtml(
        String(
          /** @type {{ sectionTitle?: string }} */ (sec).sectionTitle || "",
        ).trim(),
      );
      const items = Array.isArray(
        /** @type {{ items?: unknown[] }} */ (sec).items,
      )
        ? /** @type {{ items: Record<string, unknown>[] }} */ (sec).items
        : [];
      const body = items
        .map((row) =>
          buildCompetencyRowCells(
            row && typeof row === "object"
              ? /** @type {Record<string, unknown>} */ (row)
              : {},
            isHha,
          ),
        )
        .join("");
      return `<section class="c-sec">
        <h2 class="c-sec-h">${title || "Section"}</h2>
        <table class="c-table">
          ${isHha ? theadHha : theadRn}
          <tbody>${body}</tbody>
        </table>
      </section>`;
    })
    .join("");

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!record.applicantStatementAccepted;
  const statusCompleted =
    String(record.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  const displayName = employeeName || applicantSignedBy || "—";
  const titleMain = roleRaw
    ? `${escapeHtml(roleRaw)} competency assessment`
    : "Competency assessment";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${titleMain} — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.35in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 9px;
      line-height: 1.35;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 960px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 24px 14px 36px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 26px 28px 32px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 12px;
    }
    .logo { height: 48px; width: auto; max-width: 180px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 14px; font-weight: 700; color: #111827; margin: 0 0 3px 0; }
    .title-sub { font-size: 11px; font-weight: 500; color: #2563eb; margin: 0; }
    .meta {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      gap: 10px 14px;
      margin-bottom: 14px;
    }
    .field-label {
      font-size: 8px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 3px;
      text-transform: uppercase;
    }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px 8px;
      min-height: 30px;
      background: #fff;
      font-size: 10px;
      color: #111827;
    }
    .c-sec { margin-bottom: 14px; page-break-inside: auto; }
    .c-sec-h {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-bottom: none;
      margin: 0;
      padding: 8px 10px;
      border-radius: 6px 6px 0 0;
      color: #0f172a;
    }
    .c-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 8.5px;
      border: 1px solid #e2e8f0;
      border-radius: 0 0 6px 6px;
      overflow: hidden;
    }
    .c-th {
      text-align: left;
      padding: 6px 8px;
      background: #fff;
      border-bottom: 1px solid #e5e7eb;
      font-weight: 700;
      color: #374151;
    }
    .c-th.c-c, .c-c { text-align: center; }
    .c-td {
      padding: 5px 8px;
      border-top: 1px solid #f1f5f9;
      vertical-align: top;
      color: #1e293b;
    }
    .c-skill { font-weight: 500; }
    .req { color: #dc2626; font-weight: 700; }
    .cert-box {
      margin-top: 12px;
      padding: 10px 12px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 10px; margin: 0 0 3px 0; }
    .cert-sub { font-size: 8px; color: #6b7280; margin: 0 0 8px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 10px;
    }
    .chk { width: 13px; height: 13px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 14px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 11px; font-weight: 700; margin: 0 0 8px 0; color: #111827; }
    .sig-label {
      font-size: 8px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 6px 0 3px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 24px;
      font-size: 10px;
      padding: 3px 0;
    }
    .sig-img { max-height: 40px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 8px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">${titleMain}</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="meta">
      <div>
        <div class="field-label">Employee name</div>
        <div class="field-value">${employeeName || "—"}</div>
      </div>
      <div>
        <div class="field-label">Role</div>
        <div class="field-value">${roleDisp}</div>
      </div>
      <div>
        <div class="field-label">Date of employment</div>
        <div class="field-value">${dateEmp || "—"}</div>
      </div>
      <div>
        <div class="field-label">Date completed</div>
        <div class="field-value">${dateDone || "—"}</div>
      </div>
    </div>

    ${blocksHtml || `<p style="color:#6b7280;font-size:10px;">No competency sections were returned for this record.</p>`}

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
